/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useMemo } from 'react';
import { Stack, StackProps, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import ACTypeIcon from '@stack/assets/icons/key-details/ac-max-input.svg';
import BatteryCapacityIcon from '@stack/assets/icons/key-details/battery-capacity.svg';
import BatteryTypeIcon from '@stack/assets/icons/key-details/battery-type.svg';
import DCChargingIcon from '@stack/assets/icons/key-details/dc-charging.svg';
import DCMaxInputIcon from '@stack/assets/icons/key-details/dc-max-input.svg';
import BodyTypeIcon from '@stack/assets/icons/key-details/highlight-body-type.svg';
import EngineIcon from '@stack/assets/icons/key-details/highlight-engine.svg';
import EngineCCIcon from '@stack/assets/icons/key-details/highlight-engine-cc.svg';
import SeatsIcon from '@stack/assets/icons/key-details/highlight-seats.svg';
import SegmentIcon from '@stack/assets/icons/key-details/highlight-segment.svg';
import TransmissionIcon from '@stack/assets/icons/key-details/highlight-transmission.svg';
import KMHIcon from '@stack/assets/icons/key-details/kmh.svg';
import MaxPowerIcon from '@stack/assets/icons/key-details/max-power.svg';
import MaxRangeIcon from '@stack/assets/icons/key-details/max-range.svg';
import PublicAcChargingIcon from '@stack/assets/icons/key-details/public-ac-charging.svg';
import TopSpeedIcon from '@stack/assets/icons/key-details/top-speed.svg';
import { BorderTitle } from '@stack/ui/components';

import { SpecAndFeatureDataType } from '../SpecAndFeature';

export type CarKeyDetailBoxProps = {
  data?: SpecAndFeatureDataType;
  showHeader?: boolean;
  containerProps?: StackProps;
};

/**
 * ===========================
 * MAIN
 * ===========================
 */
export const CarKeyDetailBox: React.FC<CarKeyDetailBoxProps> = (props) => {
  const { data, showHeader = true, containerProps } = props;

  // =============== VARIABLES
  const contents = useMemo(() => {
    return {
      segment: {
        icon: <SegmentIcon />,
        title: 'Segment',
        content: data?.car_segment_type_name,
      },
      bodyType: {
        icon: <BodyTypeIcon />,
        title: 'Body Type',
        content: data?.car_body_type_name,
      },
      engineModel: {
        icon: <EngineIcon />,
        title: 'Engine Model',
        content: data?.engine_model,
      },
      engineCC: {
        icon: <EngineCCIcon />,
        title: 'CC',
        content: `${data?.car_variant_cc} cc`,
      },
      transmission: {
        icon: <TransmissionIcon />,
        title: 'Transmission',
        content: data?.car_variant_transmission,
      },
      batteryType: {
        icon: <BatteryTypeIcon />,
        title: 'Battery Type',
        content: data?.battery_type,
      },
      batteryCapacity: {
        icon: <BatteryCapacityIcon />,
        title: 'Battery Capacity',
        content: data?.battery_capacity,
      },
      range: {
        icon: <MaxRangeIcon />,
        title: 'Range',
        content: data?.driving_range,
      },
      acMaxInput: {
        icon: <ACTypeIcon />,
        title: 'AC Max Input (OBC)',
        content: data?.ac_max_input,
      },
      dcMaxInput: {
        icon: <DCMaxInputIcon />,
        title: 'DC Max Input',
        content: data?.dc_max_input,
      },
      maxPower: {
        icon: <MaxPowerIcon />,
        title: 'Max Power / Torque',
        content: `${data?.kw_hp} Hp, ${data?.nm_torque} Nm`,
      },
      zeroToHundred: {
        icon: <KMHIcon />,
        title: '0-100km/h',
        content: `${data?.['0_100_km_h']} sec`,
      },
      topSpeed: {
        icon: <TopSpeedIcon />,
        title: 'Top Speed',
        content: `${data?.top_speed} km/h`,
      },
      seats: {
        icon: <SeatsIcon />,
        title: 'Seats',
        content: data?.car_seat_number,
      },
      publicAcCharging: {
        icon: <PublicAcChargingIcon />,
        title: 'Public AC Charging (10%-80%)',
        content: data?.ac_charging_time,
      },
      dcCharging: {
        icon: <DCChargingIcon />,
        title: 'DC Charging (10%-80%)',
        content: data?.dc_charging_time,
      },
    };
  }, [data]);

  const contentKeys = useMemo((): (keyof typeof contents)[] => {
    if (data?.car_fuel_type_slug === 'ev') {
      return [
        'segment',
        'bodyType',
        'batteryType',
        'batteryCapacity',
        'range',
        'acMaxInput',
        'dcMaxInput',
        'maxPower',
        'zeroToHundred',
        'topSpeed',
        'publicAcCharging',
        'dcCharging',
      ];
    }

    if (data?.car_fuel_type_slug === 'phev') {
      return [
        'segment',
        'bodyType',
        'batteryType',
        'batteryCapacity',
        'range',
        'acMaxInput',
        'dcMaxInput',
        'engineModel',
        'engineCC',
        'transmission',
        'maxPower',
        'zeroToHundred',
        'topSpeed',
        'publicAcCharging',
        'seats',
      ];
    }

    return [
      'segment',
      'bodyType',
      'engineModel',
      'engineCC',
      'transmission',
      'maxPower',
      'zeroToHundred',
      'topSpeed',
      'seats',
    ];
  }, [data]);

  // =============== RENDERER
  const renderContentItem = useCallback(
    (icon: React.ReactNode, title: string, content?: string | number) => {
      return (
        <Stack flexDirection={'row'} gap={2}>
          <Box>{icon}</Box>
          <Typography
            fontWeight={500}
            fontSize={'0.875rem'}
            color="#67708C"
            style={{ flexGrow: 1 }}
          >
            {title}
          </Typography>
          <Typography
            textAlign={'right'}
            fontWeight={500}
            fontSize={'0.875rem'}
          >
            {content || '-'}
          </Typography>
        </Stack>
      );
    },
    []
  );

  const renderContent = useCallback(() => {
    return (
      <Stack>
        <Stack
          padding={5}
          gap={3}
          justifyContent={'center'}
          border={'1px solid #EDECED'}
          {...containerProps}
        >
          {contentKeys?.map((key) => {
            const contentItem = contents[key];
            return (
              <Stack key={key}>
                {renderContentItem(
                  contentItem.icon,
                  contentItem.title,
                  contentItem.content
                )}
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    );
  }, [containerProps, contentKeys, contents, renderContentItem]);

  // =============== VIEWS
  if (!showHeader) {
    return renderContent();
  }

  return (
    <>
      <BorderTitle title={'Key Details'}>
        {showHeader && (
          <>
            {/* <BorderTitle title={'Key Details'} /> */}
            <Stack
              border={'1px solid #EDECED'}
              borderBottom={0}
              alignItems={'center'}
              direction={'row'}
              gap={2.5}
              marginTop={5}
              padding={'12px'}
              justifyContent={'flex-start'}
            >
              <Typography fontSize={14} fontWeight={500}>
                {data?.model}
              </Typography>
            </Stack>
          </>
        )}
        {renderContent()}
      </BorderTitle>
    </>
  );
};

/**
 * ===========================
 * EXPORTS
 * ===========================
 */
export default CarKeyDetailBox;
